import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loginAttempts, setLoginAttempts] = useState(0);

  const MAX_ATTEMPTS = parseInt(import.meta.env.VITE_MAX_LOGIN_ATTEMPTS) || 5;
  const LOCKOUT_TIME = 15 * 60 * 1000; // 15 minutes

  useEffect(() => {
    const token = Cookies.get('auth_token');
    const attempts = parseInt(localStorage.getItem('login_attempts') || '0');
    const lockoutTime = localStorage.getItem('lockout_time');
    
    if (lockoutTime && Date.now() < parseInt(lockoutTime)) {
      setLoginAttempts(MAX_ATTEMPTS);
    } else {
      setLoginAttempts(attempts);
      if (lockoutTime) {
        localStorage.removeItem('lockout_time');
        localStorage.removeItem('login_attempts');
      }
    }

    if (token === 'authenticated') {
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, [MAX_ATTEMPTS]);

  const login = (password) => {
    const correctPassword = import.meta.env.VITE_LOGIN_PASSWORD || 'admin123';
    
    if (loginAttempts >= MAX_ATTEMPTS) {
      const lockoutTime = localStorage.getItem('lockout_time');
      if (!lockoutTime || Date.now() >= parseInt(lockoutTime)) {
        setLoginAttempts(0);
        localStorage.removeItem('login_attempts');
        localStorage.removeItem('lockout_time');
      } else {
        throw new Error('Account temporarily locked due to too many failed attempts. Please try again later.');
      }
    }

    if (password === correctPassword) {
      setIsAuthenticated(true);
      setLoginAttempts(0);
      Cookies.set('auth_token', 'authenticated', { expires: 1 }); // 1 day
      localStorage.removeItem('login_attempts');
      localStorage.removeItem('lockout_time');
      return true;
    } else {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('login_attempts', newAttempts.toString());
      
      if (newAttempts >= MAX_ATTEMPTS) {
        const lockoutUntil = Date.now() + LOCKOUT_TIME;
        localStorage.setItem('lockout_time', lockoutUntil.toString());
        throw new Error('Too many failed attempts. Account locked for 15 minutes.');
      }
      
      throw new Error(`Incorrect password. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    Cookies.remove('auth_token');
  };

  const isLockedOut = () => {
    const lockoutTime = localStorage.getItem('lockout_time');
    return lockoutTime && Date.now() < parseInt(lockoutTime);
  };

  const getRemainingLockoutTime = () => {
    const lockoutTime = localStorage.getItem('lockout_time');
    if (!lockoutTime) return 0;
    return Math.max(0, parseInt(lockoutTime) - Date.now());
  };

  const value = {
    isAuthenticated,
    isLoading,
    loginAttempts,
    maxAttempts: MAX_ATTEMPTS,
    login,
    logout,
    isLockedOut,
    getRemainingLockoutTime
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};